import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { verifyMfa, clearLoginError, logout } from '../redux/modules/user/actions';
import CommonAuthComponent from './CommonAuthComponent';
import CustomInput from '../components/common/CustomInput';

const MfaVerification = () => {
  const [mfaCode, setMfaCode] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes in seconds
  const timerRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    mfaVerifyLoading,
    loginError,
    sessionToken,
    userDetailsLoading,
    // Add new MFA setup fields
    isFirstTimeSetup,
    qrCodeImage,
    secretKey,
    // Add username for manual entry display
    userInfo
  } = useSelector((state) => state.users);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mfaCode && sessionToken) {
      dispatch(verifyMfa(sessionToken, mfaCode, navigate));
    }
  };

  const handleBackToLogin = async () => {
    await dispatch(clearLoginError());
    window.location.href = '/login';
  };

  const handleMfaCodeChange = (e) => {
    const value = e.target.value;
    if (/^\d{0,6}$/.test(value)) {
      setMfaCode(value);
    }
  };

  // Tick the timer down every second - no side effects in the updater
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeRemaining((prevTime) => Math.max(0, prevTime - 1));
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Handle expiry separately so side effects never run inside the state updater
  useEffect(() => {
    if (timeRemaining !== 0) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    dispatch(logout());
    navigate('/login?reason=mfa-expired');
  }, [timeRemaining, dispatch, navigate]);

  // Format time remaining for display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <CommonAuthComponent innerClassName="px-8 py-8 !pb-10">

      {/* Heading */}
      <h1 className="text-[22px] text-center mb-[10px] font-medium leading-[30px]">
        {isFirstTimeSetup ? 'Secure Your Account' : 'Verify Your Identity'}
      </h1>

      {/* Description */}
      <p className="text-center text-[16px] mb-[20px] font-normal leading-[22px]">
        {isFirstTimeSetup 
          ? 'Scan the QR code below using your preferred authenticator app, then enter the one-time code below.'
          : 'Check your preferred one-time password application for a code.'
        }
      </p>

      {/* Timer Display */}
      <div className="text-center mb-4">
        <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <svg className="w-5 h-5 text-REELTeal mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-REELTeal font-medium">
            Time remaining: {formatTime(timeRemaining)}
          </span>
        </div>
      </div>

      {/* QR Code Section - Only show for first-time setup */}
      {isFirstTimeSetup && qrCodeImage && (
        <div className="mb-4">
          {/* QR Code Container */}
          <div className="flex justify-center mb-[20px]">
            <div>
              <img 
                src={qrCodeImage} 
                alt="MFA QR Code" 
                className="w-[120px] h-[120px] object-contain"
              />
            </div>
          </div>

          {/* Manual Entry Option */}
          {/* <div className="text-center mb-4">
            <details className="inline-block">
              <summary className="text-center text-[16px] mb-[26px] font-normal leading-[22px]">
                Trouble scanning? Click for manual entry
              </summary>
              <div className="mt-3 p-4 bg-gray-50 rounded-lg text-left">
                <p className="text-sm text-gray-600 mb-2">
                  If you can&apos;t scan the QR code, manually enter this information in your authenticator app:
                </p>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Account:</span> {userInfo?.username || 'Your username'}</p>
                  <p><span className="font-medium">Secret Key:</span> <code className="bg-gray-200 px-2 py-1 rounded text-xs font-mono">{secretKey}</code></p>
                </div>
              </div>
            </details>
          </div> */}

          {/* Separator */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500 font-medium">THEN</span>
            </div>
          </div>
        </div>
      )}

      {!userDetailsLoading ? (
        <form onSubmit={handleSubmit} className="space-y-4 mt-[30px]">
          {/* Code Input */}
          <CustomInput
            label="Enter your one-time code"
            placeholder="Enter your one-time code"
            name="mfaCode"
            type="text"
            value={mfaCode}
            onChange={handleMfaCodeChange}
            maxLength={6}
            required
            autoFocus
            hideHistory={true}
            className='bg-white'
          />

          {/* Error */}
          {loginError && (
            <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-sm">
              {loginError}
            </div>
          )}

          {/* Verify Button */}
          <button
            type="submit"
            disabled={mfaVerifyLoading || mfaCode.length !== 6}
            className="w-full inline-flex disabled:opacity-50 rounded-[4px] border transition-all duration-300 text-body-lg items-center font-semibold justify-center px-6 py-3.5
            bg-REELTeal border border-transparent hover:bg-teal-200 text-white"
          >
            {mfaVerifyLoading ? (
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : null}
            {mfaVerifyLoading ? 'Verifying...' : (isFirstTimeSetup ? 'Complete Setup' : 'Verify Code')}
          </button>

          {/* Back to Login */}
          <div className="text-center">
            <button
              type="button"
              onClick={handleBackToLogin}
              className="w-full inline-flex disabled:opacity-50 rounded-[4px] border transition-all duration-300 text-body-lg items-center font-semibold justify-center px-6 py-3.5"
            >
              Back to Login
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-center">
            <svg
              className="animate-spin text-blue-600"
              width="64"
              height="64"
              viewBox="0 0 38 38"
              xmlns="http://www.w3.org/2000/svg"
              stroke="currentColor"
            >
              <g fill="none" fillRule="evenodd">
                <g transform="translate(1 1)" strokeWidth="2">
                  <circle strokeOpacity=".3" cx="18" cy="18" r="18" />
                  <path d="M36 18c0-9.94-8.06-18-18-18">
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      from="0 18 18"
                      to="360 18 18"
                      dur="1s"
                      repeatCount="indefinite"
                    />
                  </path>
                </g>
              </g>
            </svg>
          </div>
        </div>
      )}
    </CommonAuthComponent>
  );
};

export default MfaVerification;
